// Unsized container fills its parent, and a spaced container keeps the half-spacing vertical margin and padding
const gridContainer = ({ ownerState, theme }) => {
  const half = typeof ownerState.spacing === "number" ? parseFloat(theme.spacing(ownerState.spacing)) / 2 : 0;
  return {
    ...(Object.keys(ownerState.size || {}).length ? {} : { width: "100%" }),
    ...(half ? { marginTop: `-${half}px`, marginBottom: `-${half}px`, paddingTop: `${half}px`, paddingBottom: `${half}px` } : {})
  };
};

// Body font family and line height
const overrides = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Ubuntu", sans-serif',
        lineHeight: 1.43
      }
    }
  },
  // Input line height, input height and paddings
  MuiInputBase: {
    styleOverrides: {
      root: { lineHeight: "1.1876em" },
      input: ({ ownerState }) => (ownerState.multiline ? {} : { height: "1.1876em" })
    }
  },
  MuiInput: {
    styleOverrides: {
      root: ({ ownerState }) =>
        ownerState.multiline && ownerState.size !== "small" ? { padding: "6px 0 7px" } : {},
      input: ({ ownerState }) =>
        ownerState.multiline || ownerState.size === "small" ? {} : { paddingTop: 6, paddingBottom: 7 }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ ownerState }) =>
        ownerState.multiline && ownerState.size !== "small" ? { padding: "18.5px 14px" } : {},
      input: ({ ownerState }) =>
        ownerState.multiline || ownerState.size === "small" ? {} : { paddingTop: 18.5, paddingBottom: 18.5 }
    }
  },
  // Dense field input paddings and unshrunk label positions
  MuiFormControl: {
    styleOverrides: {
      root: {
        "&.MuiFormControl-marginDense": {
          "& .MuiOutlinedInput-input:not(textarea)": { paddingTop: 10.5, paddingBottom: 10.5 },
          "& .MuiInputBase-multiline.MuiOutlinedInput-root": { padding: "10.5px 14px" },
          "& .MuiInput-input:not(textarea)": { paddingTop: 3 },
          "& .MuiPickersInputBase-sectionsContainer": { paddingTop: 3.75 },
          "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 12px) scale(1)" },
          "& .MuiInputLabel-standard:not(.MuiInputLabel-shrink)": { transform: "translate(0, 21px) scale(1)" }
        }
      }
    }
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ ownerState }) =>
        ownerState.dividers ? {} : { padding: "8px 24px", "&:first-child": { paddingTop: 20 } }
    }
  },
  MuiFormLabel: {
    styleOverrides: { root: { lineHeight: 1, color: "rgba(0, 0, 0, 0.54)" } }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ ownerState }) => {
        if (!ownerState.formControl || ownerState.size === "small") return {};
        const x = ownerState.variant === "outlined" ? "14px" : "0";
        const y = ownerState.variant === "outlined" ? ["20px", "-6px"] : ["24px", "1.5px"];
        return { transform: `translate(${x}, ${ownerState.shrink ? y[1] : y[0]}) scale(${ownerState.shrink ? 0.75 : 1})` };
      }
    }
  },
  MuiSelect: {
    styleOverrides: { select: { minHeight: "1.1876em" } }
  },
  // Autocomplete input paddings, regular and small outlined
  MuiAutocomplete: {
    styleOverrides: {
      paper: { margin: "4px 0" },
      listbox: ({ theme }) => ({
        "& .MuiAutocomplete-option": {
          "&[aria-selected=true]": { backgroundColor: theme.palette.action.selected },
          "&.Mui-focused, &.Mui-focusVisible, &[aria-selected=true].Mui-focused, &[aria-selected=true].Mui-focusVisible": {
            backgroundColor: theme.palette.action.hover
          }
        }
      }),
      root: {
        "&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root": { paddingRight: "65px" },
        "& .MuiOutlinedInput-root .MuiAutocomplete-input": { padding: "9.5px 4px 9.5px 6px" },
        "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": { padding: "4.5px 4px 4.5px 6px" }
      }
    }
  },
  // An outlined chip keeps the filled label padding, the border does not take 1px from it
  MuiChip: {
    styleOverrides: {
      label: ({ ownerState }) =>
        ownerState.variant === "outlined" ? (ownerState.size === "small" ? { paddingLeft: 8, paddingRight: 8 } : { paddingLeft: 12, paddingRight: 12 }) : {}
    }
  },
  MuiRating: {
    styleOverrides: { root: { color: "#ffb400" } }
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: { height: "0.01em" }
    }
  },
  // The picker field text and its 163px width match a standard input
  MuiPickersInputBase: {
    styleOverrides: {
      root: { letterSpacing: "normal" },
      sectionsContainer: { padding: "6.75px 0 6.25px", width: "163px" },
      section: { lineHeight: "1.1876em" },
      content: { lineHeight: "1.1876em" }
    }
  },
  // Calendar day weight, and the selected day keeps the primary color on focus and hover
  MuiPickerDay: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontWeight: 300,
        "&.Mui-selected": { fontWeight: 600 },
        "&.Mui-selected:focus, &.Mui-selected:hover": { backgroundColor: theme.palette.primary.main }
      })
    }
  },
  MuiGrid: {
    styleOverrides: {
      container: gridContainer
    }
  },
  MuiCardHeader: {
    styleOverrides: {
      avatar: { display: "block" },
      action: {
        marginRight: "-4px",
        marginBottom: 0
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      paddingNone: { "&:last-child": { padding: 0 } }
    }
  },
  MuiTablePagination: {
    styleOverrides: {
      select: { paddingTop: 6, paddingBottom: 7 }
    }
  },
  MuiIconButton: {
    styleOverrides: {
      sizeLarge: { fontSize: "1.5rem" }
    }
  },
  // A selected or keyboard focused item is grey, and a divider after an item has no margin
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
          backgroundColor: theme.palette.action.selected
        },
        "& + .MuiDivider-root": { marginTop: 0, marginBottom: 0 }
      })
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: { margin: 0, fontSize: "0.625rem", lineHeight: "1.4em", backgroundColor: "rgba(97, 97, 97, 0.9)" }
    }
  },
  // Calendar popup size and grid positions
  MuiPickersCalendarHeader: {
    styleOverrides: {
      label: { fontWeight: 400 }
    }
  },
  MuiDateCalendar: {
    styleOverrides: {
      root: { width: 310, height: 305 }
    }
  },
  MuiDayCalendar: {
    styleOverrides: {
      weekDayLabel: ({ theme }) => ({ height: 26.5, color: theme.palette.text.disabled }),
      weekContainer: { margin: 0 },
      slideTransition: { minHeight: 216 }
    }
  }
};

export default overrides;
